import { userLang } from '@utils/constants';
import { win } from '@utils/win';
import { last } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';

interface SpeechToTextHookReturn {
  transcript: string;
  isListening: boolean;
  startListening: () => void;
  stopListening: () => void;
}

export const useSpeechToText = (): SpeechToTextHookReturn => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<InstanceType<
    typeof win.webkitSpeechRecognition
  > | null>(null);

  useEffect(() => {
    // Check if the browser supports WebKit Speech Recognition
    const SpeechRecognition =
      win.webkitSpeechRecognition || win.SpeechRecognition;
    if (!SpeechRecognition) {
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false; // Set to true if you want continuous results
    recognition.interimResults = false; // Set to true if you want interim results
    recognition.lang = userLang;

    recognition.onstart = (): void => {
      setIsListening(true);
    };

    recognition.onresult = (event: anyOk): void => {
      const lastResult = last(event.results) as SpeechRecognitionResult;
      if (lastResult?.isFinal) {
        setTranscript(lastResult[0]?.transcript || '');
      }
    };

    recognition.onend = (): void => {
      setIsListening(false);
    };

    recognition.onerror = (): void => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
  }, []);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start();
    }
  }, [isListening]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  }, [isListening]);

  return {
    transcript,
    isListening,
    startListening,
    stopListening,
  };
};
